/* eslint func-style: "off" */
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';
import moment from 'moment';
import queryString from 'query-string';
import omit from 'lodash/omit';

import {
  FETCH_STORIES,
  FETCH_UNSHOWN_STORIES,
  fetchStoriesFailed,
  fetchStoriesSucceeded,
  fetchUnshownStoriesSucceeded,
} from './SyndicatedStoryModal.ducks';
import apiClient from '../../helpers/apiClient';
import {NOW, convertMomentToApiTime} from '../../helpers/timeFormat';


// Selectors
const getIsRestrictSearchByDates = (state) => state.SyndicatedStoryModal.isRestrictSearchByDates;
const getNewsroomUrl = (state) => state.SyndicatedStoryModal.newsroomUrl;
const getStoryTitle = (state) => state.SyndicatedStoryModal.story.title;

/**
 * Get the oldest start time and newest end time.
 * Depending on the environment the last date available to consult.
 * At staging it can start from 3 months ago.
 * At production it can start from Jan 01 of 2012.
 * However, the client set 2012 and back-end should handle any environment.
 * @returns {{start_time: string, end_time: string}}
 * @private
 */
const _getLongestStartAndEndTime = () => {
  const campaignStartingMoment = Account && Account.getCampaignCreationMoment && Account.getCampaignCreationMoment();
  const defaultTimeStart = moment.utc().subtract(2, 'years');
  const startTime = campaignStartingMoment ? campaignStartingMoment : defaultTimeStart;
  return {
    start_time: convertMomentToApiTime(startTime),
    end_time: NOW
  };
};

/**
 * The newsroom URL to filter by title is hard to obtain. It's constructed by
 * backbone with some undesired params such as collapsed view flag turned on.
 * This method will strip these out and make sure to target the right endpoint with the right params.
 * @param newsroomUrl String composed of endpoint base path plus params.
 * @param storyTitle Title to be used as a filter.
 * @param isRestrictSearchByDates Flag to restrict by dates or not.
 * When restrict by dates is disabled it will try to search for the largest period available.
 * It's dependent on the environment running, Staging can do shorter date ranges than Production.
 * @returns {string} Newsroom endpoint that will filter stories by title in a specific date range or all over.
 * @private
 */
const _getNewsroomUrlToFilterByTitle = (newsroomUrl, storyTitle, isRestrictSearchByDates) => {
  const [basePath, params] = newsroomUrl.split('?');
  const paramsAsObject = queryString.parse(params);
  // Display every story available, made up number.
  paramsAsObject.iDisplayLength = 50000;
  if (!isRestrictSearchByDates) {
    Object.assign(paramsAsObject, _getLongestStartAndEndTime());
  }
  const filteredParams = queryString.stringify(omit(paramsAsObject, 'is_collapsed_view'));
  return `${basePath}?${filteredParams}&title=${encodeURIComponent(storyTitle)}`;
};


export const fetchStories = function* () {
  try {
    const isRestrictSearchByDates = yield select(getIsRestrictSearchByDates);
    const newsroomUrl = yield select(getNewsroomUrl);
    const storyTitle = yield select(getStoryTitle);
    const path = _getNewsroomUrlToFilterByTitle(newsroomUrl, storyTitle, isRestrictSearchByDates);
    const payload = yield call(apiClient, path);
    const {data: {response: {docs}}} = payload;
    yield put(fetchStoriesSucceeded(docs));
  } catch (e) {
    yield put(fetchStoriesFailed(e.body));
  }
};

export const fetchUnshownStories = function* () {
  try {
    const isRestrictSearchByDates = yield select(getIsRestrictSearchByDates);
    const newsroomUrl = yield select(getNewsroomUrl);
    const storyTitle = yield select(getStoryTitle);
    const path = _getNewsroomUrlToFilterByTitle(newsroomUrl, storyTitle, !isRestrictSearchByDates);
    const payload = yield call(apiClient, path);
    const {data: {response: {docs}}} = payload;
    yield put(fetchUnshownStoriesSucceeded(docs));
  } catch (e) {
    yield put(fetchStoriesFailed(e.body));
  }
};

export const fetchStoriesSaga = function* () {
  yield takeLatest(FETCH_STORIES, fetchStories);
};

export const fetchUnshownStoriesSaga = function* () {
  yield takeLatest(FETCH_UNSHOWN_STORIES, fetchUnshownStories);
};

const sagas = function* () {
  yield all([
    fork(fetchStoriesSaga),
    fork(fetchUnshownStoriesSaga),
  ]);
};

export default sagas;
export {
  getIsRestrictSearchByDates,
  getNewsroomUrl,
  getStoryTitle,
};
